import './thesaurus-term-chooser.scss'

import { first, mergeMap } from 'rxjs/operators'
import { defineComponent } from 'vue'
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons'

import { getTranslatedString, ThesaurusTerm, ThesaurusTermWithParents } from '@skinsoft/types'
import { thesaurusTermSelectors } from '@skinsoft/selectors'
import { reactiveVueMixin } from '@skinsoft/reactive-vue'

export const ThesaurusTermChooser = defineComponent({
    mixins: [reactiveVueMixin],
    props: {
        types: Array,
        fullWidth: {
            type: Boolean,
            default: false,
        },
        // The identifer of the thesaurus in the database (ex : material, typeOfAlteration, etc...)
        thesaurusIdentifier: {
            type: String,
            required: true,
        },
        // If not null, this term id will be used to retrieve the children terms, ands set them as select options
        // if the value is set to "root", the first root element of the thesaurus will be used
        parentTermId: {
            type: String,
        },
        // Index of the thesaurusterms tree level if no parentTermId is specified
        termLevelIndex: {
            type: Number,
        },
        // A text corresponding to the value 'null', added on top of the options
        placeholder: {
            type: String,
        },
        // The message displayed when value doesn't match options
        warningMessage: {
            type: String,
        },
        autoUpdate: {
            type: Boolean,
        },
        // getFieldStatus: (fieldName: string) => FieldStatus
        getFieldStatus: {
            type: Function,
        },
        readOnly: {
            type: Boolean,
        },
    },
    template: require('./thesaurus-term-chooser.html'),
    data() {
        return {
            // The select options
            options: [] as any[],
            // Indicates whether the current value of the term chooser is available in the options defined by the parent term
            termMatchesOptions: true,
            currentValue: "" as any,
            // Icon
            warningIcon: faExclamationCircle,
        }
    },
    created() {
        // When the underlying field's value changes, updates members and resends the event
        this.$on('field-value-changed', (message) => {
            this.$parent?.$emit('field-value-changed', message)
            this.currentValue = message.value;
            if (this.parentTermId) {
                this.termMatchesOptions = this.isValueInOptions(this.currentValue);
            }
        })
    },
    methods: {
        isValueInOptions(value) {
            return this.options.filter((el) => el.value === value).length > 0;
        },
        async subscribeToThesaurus() {
            if(this.placeholder) {
                this.options = [{
                    text: this.placeholder,
                    value: null as any,
                }]
            } else {
                this.options = []
            }

            // Getting thesaurus terms
            // If no parent term is specified
            if (!this.parentTermId) {
                // If no particular level of tree is specified, fetching root terms
                if (!this.termLevelIndex) {
                    this.subscribeTo(
                        this.$thesaurusDataStore.fetchOneByIdentifier(this.thesaurusIdentifier).pipe(
                            mergeMap(thesaurus => {
                                return this.$thesaurusTermDataStore.fetchRoot(thesaurus.id);
                            }),
                            first()
                        ),
                        (terms) => {
                            // Setting root terms as options
                            this.buildSelectOptions(terms)
                        },
                        'children-terms-subscription'
                    )
                }
                else {
                    // If a level of tree is specified ngetting all terms of this level, across all parents

                    // Sac method
                    this.subscribeTo(
                        this.$thesaurusDataStore.fetchOne(this.thesaurusIdentifier)
                            .pipe(
                                mergeMap(thesaurus => {
                                    return this.$thesaurusTermDataStore.fetchRoot(thesaurus.id);
                                })
                            ),
                        async terms => {
                            let parentTerms: ThesaurusTermWithParents[] = [];
                            let termsToKeep: ThesaurusTermWithParents[] =
                                terms.map(term => {
                                    return {
                                        parentTerms: [],
                                        term
                                    }
                                })
                            // Looping through thesaurus levels
                            for (let currentLevel = 1; currentLevel < (this.termLevelIndex ?? 0); currentLevel++) {
                                // If this is not the last level of the tree, going one level deeper
                                // Making the terms of the previous level the new parent terms, and resetting terms to keep for this level
                                parentTerms = termsToKeep;
                                termsToKeep = []
                                await Promise.all(
                                    parentTerms.map(async parentTerm => {
                                        // Fetching children terms of current term
                                        const children = await this.$thesaurusTermDataStore.fetchChildren(parentTerm.term.id, this.getAppLocale()).pipe(first()).toPromise()
                                        // Adding children terms to current level terms to keep
                                        termsToKeep.push(...children.map(child => {
                                            // Keeping track of parent terms to display the term path in select box
                                            const parents = [...parentTerm.parentTerms]
                                            parents.push(parentTerm.term)
                                            return {
                                                parentTerms: parents,
                                                term: child
                                            }
                                        }))
                                    })
                                )
                            }
                            this.buildSelectOptionsWithPaths(termsToKeep)
                        })
                }
            }
            else {
                // If parentTermId exists, fetching its children terms

                let resolvedParentTermId = this.parentTermId;
                if (this.parentTermId === "root") {
                    const thesaurus = await (this.$thesaurusDataStore.fetchOne(this.thesaurusIdentifier).pipe(first()).toPromise())
                    const root = await this.$thesaurusTermDataStore.fetchRoot(thesaurus.id).pipe(first()).toPromise();
                    resolvedParentTermId = root[0].id;
                }


                this.subscribeTo(
                    this.$thesaurusTermDataStore.fetchChildren(resolvedParentTermId, this.getAppLocale())
                        .pipe(first()),
                    (terms => {
                        // Setting root terms as options and updating chooser matching state
                        this.buildSelectOptions(terms)
                        this.termMatchesOptions = this.isValueInOptions(this.currentValue);
                    }),
                    'children-terms-subscription')
            }
        },

        /**
         * Builds the select box options with each term path between parenthesis
         * @param terms
         */
        buildSelectOptionsWithPaths(terms: ThesaurusTermWithParents[]) {
            const self = this;
            const rawOptions: Array<{ text: string, value: string }> = [];
            terms.map((term) => {
                const termValue = this.getTermTitle(term.term, self.getAppLocale())
                const parentsValue = `(${term.parentTerms.map(parent => this.getTermTitle(parent, self.getAppLocale())).join(" > ")})`
                rawOptions.push({
                    text: termValue + " " + parentsValue,
                    value: term.term.id,
                })
            });

            this.options.push(...rawOptions.sort(
                // Sorting terms alphabetically
                (term1, term2) => {
                    return term1.text.localeCompare(term2.text);
                }
            ))
        },

        getTermTitle(term: ThesaurusTerm, locale: string) {
            return getTranslatedString(thesaurusTermSelectors.preferredTerm(term), locale);
        },

        /**
         * Builds the select box options from retrieved thesaurus terms
         * @param terms
         */
        buildSelectOptions(terms: ThesaurusTerm[]) {
            const self = this;
            const rawOptions: Array<{ text: string, value: string }> = [];
            terms.map((term) => {
                rawOptions.push({
                    text: getTranslatedString(thesaurusTermSelectors.preferredTerm(term), self.getAppLocale()),
                    value: term.id,
                })
            });

            this.options.push(...rawOptions.sort(
                // Sorting terms alphabetically
                (term1, term2) => {
                    return term1.text.localeCompare(term2.text);
                }
            ))
        },

        unsubscribe() {
            this.unsubscribeFrom('children-terms-subscription')
        },
    },
    watch: {
        thesaurusIdentifier: {
            handler() {
                if (this.thesaurusIdentifier) {
                    this.subscribeToThesaurus()
                }
            },
            immediate: true,
        },
        parentTermId() {
            this.unsubscribe();
            this.subscribeToThesaurus()
        },
    },
})
